import type { FC } from "react";

import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import { ErrorState } from "@/components/common/ErrorState";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  DocxReviewRoomContext,
  type DocxReviewRoom
} from "@/features/draftEditor/reviewRoom";

import { CaseChatPanel } from "./chat/CaseChatPanel";
import { CaseUpgradeOverlay } from "./compat/CaseUpgradeOverlay";
import { ArtifactTabs } from "./components/ArtifactTabs";
import { OpenFromUrl } from "./components/OpenFromUrl";
import { CaseIdeProvider, useCaseIde } from "./context/CaseIdeContext";
import { CaseDataProvider } from "./data/CaseDataContext";
import { CaseCitationSource } from "./editor/CaseCitationSource";
import { EditorArea } from "./editor/EditorArea";
import { ExplorerDnd } from "./explorer/ExplorerDnd";
import { CaseFilesTree } from "./files/CaseFilesTree";
import { CaseyDock } from "./layout/CaseyDock";
import { useCaseyShortcut } from "./layout/caseyShortcut";
import { IdeLayout } from "./layout/IdeLayout";
import { ideLayoutStore } from "./layout/ideLayoutStore";
import { usePaneShortcuts } from "./layout/paneShortcuts";
import { useShowCaseyOnLink } from "./layout/useShowCaseyOnLink";
import { UscisPane } from "./uscis/UscisPane";
import { CaseExplorerHeader } from "./workspace/CaseExplorerHeader";
import { KeyFactsHeader } from "./workspace/KeyFactsHeader";
import { CompanyHeader } from "./workspace/CompanyHeader";
import { FilingDeadlineHeader } from "./workspace/FilingDeadlineHeader";
import { CaseWorkspaceTree } from "./workspace/CaseWorkspaceTree";
import { WorkflowStrip } from "./workflows/components/WorkflowStrip";
import { WorkflowsProvider } from "./workflows/context/WorkflowsContext";

type Props = {
  individualId: string;
  caseId?: string | null;
};

const CHAT_FAILED = (
  <ErrorState
    title="Chat Could Not Load"
    message="Something in this conversation failed to display. It was reported; reload to try again. The case and its drafts are unaffected."
    buttonCta="Reload"
    minH="100%"
  />
);

// The review rail needs ~900px; the files rail is the first thing to give.
const REVIEW_ROOM: DocxReviewRoom = {
  makeRoom: () => ideLayoutStore.makeRoom("left"),
  releaseRoom: () => ideLayoutStore.releaseRoom("left")
};

/**
 * The center column: tabs, the Case Prep strip and the open artifact. In
 * Comfortable the empty state and the Case Automations page sit on a slightly
 * tinted page so their white cards stand out; open documents keep their own
 * backgrounds (`data-view="document"`). See styles/center-pane.css.
 */
const CenterColumn: FC = () => {
  const { activeArtifact } = useCaseIde();
  const view =
    !activeArtifact || activeArtifact.kind === "workflows"
      ? "page"
      : "document";
  return (
    <div
      className="case-center-pane flex h-full min-h-0 flex-col"
      data-view={view}
    >
      <ArtifactTabs />
      <WorkflowStrip />
      <div className="case-center-body flex min-h-0 flex-1 flex-col">
        <DocxReviewRoomContext.Provider value={REVIEW_ROOM}>
          <EditorArea />
        </DocxReviewRoomContext.Provider>
      </div>
    </div>
  );
};

/**
 * The case IDE: the case header, its Key Facts / Time / USCIS rows and the
 * files + workspace trees on the left, the open artifact in the center, the
 * case agent on the right, laid out by `IdeLayout`. The case header
 * (rendered above this tree) toggles the side panels through
 * `ideLayoutStore`; while the agent panel is hidden, Casey waves from the
 * bottom-right edge to bring it back. ⌥⌘1–4 make one pane big (a maximized
 * explorer section hides the Key Facts to USCIS rows and folds the other
 * section to its header) and Escape restores.
 */
export const CaseIde: FC<Props> = ({ individualId, caseId }) => {
  useCaseyShortcut();
  usePaneShortcuts();
  useShowCaseyOnLink();

  return (
    <CaseIdeProvider individualId={individualId}>
      <CaseDataProvider caseId={caseId ?? null}>
        <WorkflowsProvider>
          <CaseCitationSource>
            <TooltipProvider>
              <OpenFromUrl />
              <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-surface">
                <CaseUpgradeOverlay />
                <IdeLayout
                  explorerHeader={
                    <CaseExplorerHeader individualId={individualId} />
                  }
                  explorerRows={
                    <>
                      <div className="flex shrink-0 flex-col px-1">
                        <KeyFactsHeader individualId={individualId} />
                        <CompanyHeader individualId={individualId} />
                        <FilingDeadlineHeader individualId={individualId} />
                      </div>
                      <UscisPane />
                    </>
                  }
                  files={<CaseFilesTree />}
                  workspace={<CaseWorkspaceTree />}
                  wrapSections={(sections) => (
                    <ExplorerDnd>{sections}</ExplorerDnd>
                  )}
                  center={<CenterColumn />}
                  right={
                    <ErrorBoundary scope="case-chat" fallback={CHAT_FAILED}>
                      <CaseChatPanel />
                    </ErrorBoundary>
                  }
                />
                <CaseyDock individualId={individualId} />
              </div>
            </TooltipProvider>
          </CaseCitationSource>
        </WorkflowsProvider>
      </CaseDataProvider>
    </CaseIdeProvider>
  );
};
